// App.tsx - Fixed diagnostic version with no TypeScript errors
import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import MainLayout from './components/layout/MainLayout/MainLayout';
import Dashboard from './pages/Dashboard/Dashboard';
import DocumentsList from './pages/Documents/DocumentsList';
import Upload from './pages/Upload/Upload';
import DocumentAnalysis from './pages/Analysis/DocumentAnalysis';
// Import react-pdf for the test
import { Document, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import GlobalChatInterface from './components/chat/GlobalChatInterface';
import { ROUTES } from './constants/routes';
import './styles/global.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// FIXED DIAGNOSTIC PDF TEST
const FixedDiagnosticTest: React.FC = () => {
  const [logs, setLogs] = useState<string[]>(['🔬 Diagnostic test ready']);
  const [testPhase, setTestPhase] = useState<'ready' | 'testing' | 'complete' | 'failed'>('ready');
  const [testResults, setTestResults] = useState<{
    step1_fetch: boolean;
    step2_size: number;
    step3_worker: boolean;
    step4_pdfjs: boolean;
    step5_reactpdf: boolean;
  }>({
    step1_fetch: false,
    step2_size: 0,
    step3_worker: false,
    step4_pdfjs: false,
    step5_reactpdf: false
  });
  const [showReactPDF, setShowReactPDF] = useState(false);

  const pdfUrl = 'http://localhost:8000/api/documents/68474b8c47959e3688674eda/pdf';

  const log = useCallback((msg: string) => {
    const time = new Date().toLocaleTimeString();
    const logMsg = `${time}: ${msg}`;
    console.log('DIAGNOSTIC:', logMsg);
    setLogs(prev => [...prev.slice(-12), logMsg]);
  }, []);

  // Step 1: Test direct fetch
  const testStep1Fetch = async () => {
    log('🔍 STEP 1: Testing PDF fetch...');
    
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      log(`✅ STEP 1 SUCCESS: PDF fetched (${Math.round(blob.size / 1024)} KB)`);
      
      setTestResults(prev => ({ 
        ...prev, 
        step1_fetch: true, 
        step2_size: blob.size 
      }));
      
      return blob;
    } catch (error) {
      log(`❌ STEP 1 FAILED: ${error}`);
      throw error;
    }
  };

  // Step 2: Test worker
  const testStep2Worker = async () => {
    log('🔍 STEP 2: Testing PDF.js worker...');
    
    try {
      const response = await fetch(pdfjs.GlobalWorkerOptions.workerSrc);
      if (!response.ok) {
        throw new Error(`Worker HTTP ${response.status}`);
      }
      
      log('✅ STEP 2 SUCCESS: Worker accessible');
      setTestResults(prev => ({ ...prev, step3_worker: true }));
      
    } catch (error) {
      log(`❌ STEP 2 FAILED: ${error}`);
      throw error;
    }
  };

  // Step 3: Test PDF.js directly
  const testStep3PDFjs = async (pdfBlob: Blob) => {
    log('🔍 STEP 3: Testing PDF.js directly...');
    
    try {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      
      const loadingTask = pdfjs.getDocument({
        data: arrayBuffer
      });
      
      const pdf = await loadingTask.promise;
      log(`✅ STEP 3 SUCCESS: PDF.js loaded ${pdf.numPages} pages`);
      
      setTestResults(prev => ({ ...prev, step4_pdfjs: true }));
      
    } catch (error) {
      log(`❌ STEP 3 FAILED: ${error}`);
      throw error;
    }
  };

  // Step 4: Test react-pdf
  const testStep4ReactPDF = () => {
    return new Promise<void>((resolve, reject) => {
      log('🔍 STEP 4: Testing react-pdf...');
      
      let completed = false;
      
      // 15 second timeout
      const timeout = setTimeout(() => {
        if (!completed) {
          completed = true;
          log('⏰ STEP 4 TIMEOUT: React-PDF taking too long');
          reject(new Error('React-PDF timeout after 15 seconds'));
        }
      }, 15000);
      
      // Store handlers globally for the Document component
      (window as any).__diagnosticHandlers = {
        onLoadSuccess: ({ numPages }: { numPages: number }) => {
          if (!completed) {
            completed = true;
            clearTimeout(timeout);
            log(`✅ STEP 4 SUCCESS: React-PDF loaded ${numPages} pages`);
            setTestResults(prev => ({ ...prev, step5_reactpdf: true }));
            resolve();
          }
        },
        onLoadError: (error: Error) => {
          if (!completed) {
            completed = true;
            clearTimeout(timeout);
            log(`❌ STEP 4 FAILED: ${error.message}`);
            reject(error);
          }
        }
      };
      
      setShowReactPDF(true);
    });
  };

  // Run all diagnostic tests
  const runDiagnosticTests = async () => {
    try {
      setTestPhase('testing');
      setTestResults({
        step1_fetch: false,
        step2_size: 0,
        step3_worker: false,
        step4_pdfjs: false,
        step5_reactpdf: false
      });
      setShowReactPDF(false);
      
      log('🚀 Starting complete diagnostic...');
      
      // Step 1: Fetch test
      const pdfBlob = await testStep1Fetch();
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay
      
      // Step 2: Worker test
      await testStep2Worker();
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 3: PDF.js test
      await testStep3PDFjs(pdfBlob);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 4: React-PDF test
      await testStep4ReactPDF();
      
      log('🎉 ALL DIAGNOSTICS PASSED!');
      setTestPhase('complete');
      
    } catch (error) {
      log(`💥 DIAGNOSTIC STOPPED: ${error}`);
      setTestPhase('failed');
      setShowReactPDF(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if ((window as any).__diagnosticHandlers) {
        delete (window as any).__diagnosticHandlers;
      }
    };
  }, []);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>🔬 PDF Integration Diagnostic</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        This will test each step of the PDF loading process to identify the exact issue.
      </p>
      
      {/* Test Status Panel */}
      <div style={{ 
        backgroundColor: '#f8f9fa', 
        border: '2px solid #007bff', 
        borderRadius: '10px', 
        padding: '25px', 
        marginBottom: '25px' 
      }}>
        <h3 style={{ color: '#007bff', marginTop: '0', marginBottom: '20px' }}>📊 Diagnostic Status</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <strong>Current Phase:</strong> 
          <span style={{ 
            marginLeft: '15px',
            padding: '6px 12px',
            borderRadius: '6px',
            fontWeight: 'bold',
            backgroundColor: 
              testPhase === 'complete' ? '#d4edda' : 
              testPhase === 'failed' ? '#f8d7da' : 
              testPhase === 'testing' ? '#fff3cd' : '#e2e3e5',
            color:
              testPhase === 'complete' ? '#155724' : 
              testPhase === 'failed' ? '#721c24' : 
              testPhase === 'testing' ? '#856404' : '#6c757d'
          }}>
            {testPhase === 'ready' && '⭕ Ready to start'}
            {testPhase === 'testing' && '🔄 Running tests...'}
            {testPhase === 'complete' && '✅ All tests passed!'}
            {testPhase === 'failed' && '❌ Tests failed'}
          </span>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '15px', 
          marginBottom: '25px' 
        }}>
          <div style={{ 
            padding: '10px',
            backgroundColor: testResults.step1_fetch ? '#d4edda' : '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid ' + (testResults.step1_fetch ? '#c3e6cb' : '#dee2e6'),
          }}>
            <strong>Step 1 - PDF Fetch:</strong><br/>
            <span style={{ color: testResults.step1_fetch ? '#155724' : '#6c757d' }}>
              {testResults.step1_fetch ? '✅ Success' : '⭕ Pending'}
            </span>
            {testResults.step2_size > 0 && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                Size: {Math.round(testResults.step2_size / 1024)} KB
              </div>
            )}
          </div>
          
          <div style={{ 
            padding: '10px',
            backgroundColor: testResults.step3_worker ? '#d4edda' : '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid ' + (testResults.step3_worker ? '#c3e6cb' : '#dee2e6'),
          }}>
            <strong>Step 2 - Worker:</strong><br/>
            <span style={{ color: testResults.step3_worker ? '#155724' : '#6c757d' }}>
              {testResults.step3_worker ? '✅ Success' : '⭕ Pending'}
            </span>
          </div>
          
          <div style={{ 
            padding: '10px',
            backgroundColor: testResults.step4_pdfjs ? '#d4edda' : '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid ' + (testResults.step4_pdfjs ? '#c3e6cb' : '#dee2e6'),
          }}>
            <strong>Step 3 - PDF.js:</strong><br/>
            <span style={{ color: testResults.step4_pdfjs ? '#155724' : '#6c757d' }}>
              {testResults.step4_pdfjs ? '✅ Success' : '⭕ Pending'}
            </span>
          </div>
          
          <div style={{ 
            padding: '10px',
            backgroundColor: testResults.step5_reactpdf ? '#d4edda' : '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid ' + (testResults.step5_reactpdf ? '#c3e6cb' : '#dee2e6'),
          }}>
            <strong>Step 4 - React-PDF:</strong><br/>
            <span style={{ color: testResults.step5_reactpdf ? '#155724' : '#6c757d' }}>
              {testResults.step5_reactpdf ? '✅ Success' : '⭕ Pending'}
            </span>
          </div>
        </div>

        <button
          onClick={runDiagnosticTests}
          disabled={testPhase === 'testing'}
          style={{
            padding: '15px 30px',
            backgroundColor: testPhase === 'testing' ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: testPhase === 'testing' ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          {testPhase === 'testing' ? '⏳ Running Tests...' : '🚀 Start Diagnostic'}
        </button>
      </div>

      {/* Debug Log */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        padding: '20px', 
        marginBottom: '20px' 
      }}>
        <h3 style={{ marginTop: '0', color: '#495057' }}>📝 Diagnostic Log</h3>
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #e9ecef',
          borderRadius: '6px', 
          padding: '15px',
          height: '200px',
          overflowY: 'auto',
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '13px',
          lineHeight: '1.4'
        }}>
          {logs.map((logEntry, i) => (
            <div key={i} style={{ 
              marginBottom: '6px',
              color: logEntry.includes('❌') ? '#dc3545' : 
                    logEntry.includes('✅') ? '#28a745' : 
                    logEntry.includes('🔍') ? '#007bff' : '#495057'
            }}>
              {logEntry}
            </div>
          ))}
        </div>
      </div>

      {/* React-PDF Test Component */}
      {showReactPDF && (
        <div style={{ 
          backgroundColor: '#fff8e1', 
          border: '2px solid #ffc107', 
          borderRadius: '8px', 
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ color: '#f57f17', marginTop: '0' }}>📄 React-PDF Live Test</h3>
          <p style={{ color: '#ef6c00' }}>Testing react-pdf component (15 second timeout)...</p>
          
          <div style={{ border: '1px dashed #ffc107', borderRadius: '6px', padding: '15px' }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={(window as any).__diagnosticHandlers?.onLoadSuccess}
              onLoadError={(window as any).__diagnosticHandlers?.onLoadError}
              loading={
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #fff3e0',
                    borderTop: '5px solid #ff9800',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }}></div>
                  <h4 style={{ color: '#f57f17' }}>Testing React-PDF...</h4>
                  <p style={{ color: '#ef6c00' }}>This is where it usually gets stuck</p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              }
              error={
                <div style={{ 
                  padding: '30px', 
                  color: '#d32f2f', 
                  textAlign: 'center',
                  backgroundColor: '#ffebee',
                  borderRadius: '6px'
                }}>
                  <h4>❌ React-PDF Error Detected</h4>
                  <p>This means react-pdf specifically has issues</p>
                </div>
              }
              options={{
                cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                cMapPacked: true,
                standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`
              }}
            />
          </div>
        </div>
      )}

      {/* Results */}
      {testPhase === 'complete' && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724',
          border: '2px solid #c3e6cb', 
          borderRadius: '10px', 
          padding: '25px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginTop: '0' }}>🎉 All Diagnostics Passed!</h2>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            React-PDF is working correctly. The issue is likely with component state management or rendering logic.
          </p>
          <p style={{ fontSize: '14px', color: '#0f5132' }}>
            You can now implement a simple, stable PDF viewer in your DocumentAnalysis component.
          </p>
        </div>
      )}

      {testPhase === 'failed' && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          border: '2px solid #f5c6cb', 
          borderRadius: '10px', 
          padding: '25px',
          textAlign: 'center'
        }}>
          <h2 style={{ marginTop: '0' }}>💥 Diagnostic Failed</h2>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            Check the diagnostic log above to see exactly which step failed and why.
          </p>
          <button
            onClick={runDiagnosticTests}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            🔄 Retry Diagnostic
          </button>
        </div>
      )}

      {/* Quick Links */}
      <div style={{ marginTop: '30px', textAlign: 'center' }}>
        <a 
          href={pdfUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ 
            display: 'inline-block',
            padding: '12px 24px', 
            backgroundColor: '#28a745', 
            color: 'white', 
            textDecoration: 'none',
            borderRadius: '6px',
            marginRight: '15px'
          }}
        >
          🔗 Test Direct PDF
        </a>
        <a 
          href="http://localhost:3000/dashboard" 
          style={{ 
            display: 'inline-block',
            padding: '12px 24px', 
            backgroundColor: '#6c757d', 
            color: 'white', 
            textDecoration: 'none',
            borderRadius: '6px'
          }}
        >
          ← Back to Dashboard
        </a>
      </div>
    </div>
  );
};

// MAIN APP COMPONENT
const App: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const chatConfig = {
    apiEndpoint: '/query',
    apiHeaders: {
      'Content-Type': 'application/json',
    }
  };

  return (
    <Provider store={store}>
      <Router>
        <div className="App">
          <Routes>
            {/* FIXED DIAGNOSTIC PDF TEST ROUTE */}
            <Route path="/pdf-test" element={<FixedDiagnosticTest />} />
            
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/documents" element={<DocumentsList />} />
            <Route path="/analysis/:documentId" element={<DocumentAnalysis />} />
            <Route path={ROUTES.UPLOAD} element={<Upload />} />
            
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path={ROUTES.CHAT} element={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h2>Ask Arti - Coming Soon</h2>
                  <p>Powered by Artifi Agentic Studio</p>
                </div>
              } />
              <Route path={ROUTES.SETTINGS} element={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h2>Settings - Coming Soon</h2>
                  <p>Powered by Artifi Agentic Studio</p>
                </div>
              } />
              <Route path="*" element={
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                  <h2>Page Not Found</h2>
                  <p>Return to <a href="/dashboard">Dashboard</a></p>
                </div>
              } />
            </Route>
          </Routes>
        </div>
      </Router>
    </Provider>
  );
};

export default App;